import React, { useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import SSFilterFields from './SSFilterFields';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import { reportNameFormat } from '../../utils/commonUtils';
import toastCenter from '../../utils/toastCenter';
import APPCONSTANTS from '../../constants/appConstants';
import { IDateRangeFormSlice } from './DateRangeFields';
import { IRegionFiltersFormValues } from './RegionFilterFields';
import Loader from '../../components/loader/Loader';
import { ssReportListSelector, ssReportLoadingSelector, ssReportTotalSelector } from '../../store/report/selectors';
import ERRORS from '../../constants/error';
import { downloadSSReportExcel } from '../../services/reportAPI';
import { fetchSSReportViewRequest } from '../../store/report/actions';
import { ISSReportViewRequestPayload } from '../../store/report/types';

export interface ISSFiltersFormValues extends IDateRangeFormSlice, IRegionFiltersFormValues {
  branches: any[];
  userRoles: any[];
  userNames: any[];
}

interface ISSReportFormState {
  ssreport: ISSFiltersFormValues;
}

export const validateSSReportFilters = (formValues: ISSFiltersFormValues): boolean =>
  Boolean(
    formValues?.fromDate &&
      formValues?.toDate &&
      formValues?.division?.name &&
      formValues?.district?.name &&
      formValues?.upazila?.name &&
      formValues?.branches?.length &&
      formValues?.userRoles?.length &&
      formValues?.userNames?.length
  );

const buildSSReportPayload = (
  formValues: ISSFiltersFormValues,
  pagination?: { skip: number; limit: number }
): ISSReportViewRequestPayload | null => {
  const { fromDate, toDate, division, district, upazila, branches, userRoles, userNames } = formValues;

  if (!validateSSReportFilters(formValues)) {
    return null;
  }

  return {
    startDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
    endDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
    division: division.name,
    district: district.name,
    upazila: upazila.name,
    branches: (branches || [])
      .map((branch) => branch?.code)
      .filter((code): code is string => Boolean(code)),
    roles: (userRoles || [])
      .map((role) => role?.name)
      .filter((role): role is string => Boolean(role)),
    providerNames: (userNames || [])
      .map((user) => user?.username)
      .filter((name): name is string => Boolean(name)),
    skip: pagination?.skip,
    limit: pagination?.limit
  };
};

const SSReport = (): React.ReactElement => {
  const dispatch = useDispatch();
  const reportRows = useSelector(ssReportListSelector);
  const isReportLoading = useSelector(ssReportLoadingSelector);
  const totalCount = useSelector(ssReportTotalSelector);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [page, setPage] = useState(APPCONSTANTS.NUMBER.ONE);
  const [rowsPerPage, setRowsPerPage] = useState(APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE);
  const initialValues = useMemo(
    () => ({
      ssreport: {
        fromDate: undefined,
        toDate: undefined,
        division: null,
        district: null,
        upazila: null,
        branches: [],
        userRoles: [],
        userNames: []
      }
    }),
    []
  );

  const handleDownloadReport = async (values: ISSReportFormState) => {
    if (isDownloadLoading) {
      return;
    }

    const payload = buildSSReportPayload(values?.ssreport || ({} as ISSFiltersFormValues));
    if (!payload) {
      toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.SS_REPORT_FILTER_VALIDATION_ERROR);
      return;
    }

    try {
      setIsDownloadLoading(true);
      await downloadSSReportExcel(payload);
      toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
    } catch (error) {
      if ((error as Error)?.message === ERRORS.NETWORK_ERROR.message) {
        toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
      } else {
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.DOWNLOAD_FAIL_MSG);
      }
    } finally {
      setIsDownloadLoading(false);
    }
  };

  const fetchPaginatedSSReport = (formValues: ISSFiltersFormValues, nextPage: number, nextRowsPerPage: number) => {
    const payload = buildSSReportPayload(formValues, {
      skip: (nextPage - 1) * nextRowsPerPage,
      limit: nextRowsPerPage
    });

    if (!payload) {
      return;
    }

    dispatch(
      fetchSSReportViewRequest({
        payload,
        failureCb: (error) => {
          if (error.message === ERRORS.NETWORK_ERROR.message) {
            toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
          } else {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SS_REPORT_FETCH_ERROR);
          }
        }
      })
    );
  };

  return (
    <Form<ISSReportFormState>
      onSubmit={() => undefined}
      initialValues={initialValues}
      render={({ form }) => (
        <div className='row'>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <SSFilterFields
                form={form}
                name='ssreport'
                values={values || {}}
                rowsPerPage={rowsPerPage}
                onFetchSSReport={(formValues, nextPage, nextRowsPerPage) => {
                  setPage(nextPage);
                  fetchPaginatedSSReport(formValues, nextPage, nextRowsPerPage);
                }}
              />
            )}
          </FormSpy>
          <div className='col'>
            <DetailCard
              header={reportNameFormat(APPCONSTANTS.REPORT_LIST.SS_REPORT)}
              buttonLabel={isDownloadLoading ? 'Exporting...' : 'Export'}
              onButtonClick={() => handleDownloadReport(form.getState().values)}
              isDownload={true}
              isDisabled={isDownloadLoading}
            >
              {isReportLoading ? (
                <Loader />
              ) : (
                <CustomTable
                  rowData={reportRows}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={totalCount}
                  handlePageChange={(pageNo: number, nextRowsPerPage?: number) => {
                    const updatedRowsPerPage = nextRowsPerPage || rowsPerPage;
                    setPage(pageNo);
                    setRowsPerPage(updatedRowsPerPage);
                    const values = form.getState().values;
                    fetchPaginatedSSReport(
                      values?.ssreport ?? {},
                      pageNo,
                      updatedRowsPerPage
                    );
                  }}
                  columnsDef={[
                    {
                      id: 1,
                      name: 'serviceProvidedBy',
                      label: 'Service Provided By'
                    },
                    {
                      id: 2,
                      name: 'skUserId',
                      label: 'SK User Id'
                    },
                    {
                      id: 3,
                      name: 'skName',
                      label: 'SK Name'
                    },
                    {
                      id: 4,
                      name: 'ssName',
                      label: 'SS Name'
                    },
                    {
                      id: 5,
                      name: 'newPregnantWomen',
                      label: 'New Pregnant Women (1st Trimester)'
                    },
                    {
                      id: 6,
                      name: 'ancService',
                      label: 'ANC Service'
                    },
                    {
                      id: 7,
                      name: 'pncWithin72hrs',
                      label: 'pnc within 72hrs'
                    },
                    {
                      id: 8,
                      name: 'ncdService',
                      label: 'NCD Service'
                    },
                    {
                      id: 9,
                      name: 'readingGlassSold',
                      label: 'Reading Glass Sold'
                    }
                  ]}
                  isDelete={false}
                  isEdit={false}
                  isView={false}
                  isDownload={false}
                />
              )}
            </DetailCard>
          </div>
        </div>
      )}
    />
  );
};

export default SSReport;
